import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Calendar as CalendarIcon, FileText, Database, Download, Loader2, Search, CheckSquare } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from '@/components/ui/use-toast';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import JSZip from 'jszip';
import api from '@/lib/api';
import { DisparoLote } from '@/contexts/DisparosContext';

interface DisparoBoleto {
  id: number;
  lote_id: number;
  nome: string;
  placa: string;
  modelo: string;
  valor: number;
  vencimento: string;
  contato: string;
  status: string;
  data_envio?: string;
  hora_envio?: string;
  created_at: string;
}

interface ExportarLotesModalProps {
  isOpen: boolean;
  onClose: () => void;
  lotes: DisparoLote[];
  user: any;
  clienteId: number | null;
  selectedLoteIds?: number[];
  onSelectionModeChange?: (enabled: boolean) => void;
}

const ExportarLotesModal: React.FC<ExportarLotesModalProps> = ({
  isOpen,
  onClose,
  lotes,
  user,
  clienteId,
  selectedLoteIds: propSelectedLoteIds = [],
  onSelectionModeChange
}) => {
  const [exportFormat, setExportFormat] = useState<'pdf' | 'xlsx'>('pdf');
  const [exportOrganization, setExportOrganization] = useState<'single' | 'multiple'>('single');
  const [loteSelection, setLoteSelection] = useState<'all' | 'period' | 'specific'>('all');
  const [periodFilter, setPeriodFilter] = useState('this_month');
  const [customDateFrom, setCustomDateFrom] = useState<Date | undefined>(undefined);
  const [customDateTo, setCustomDateTo] = useState<Date | undefined>(undefined);
  const [isFromCalendarOpen, setIsFromCalendarOpen] = useState(false);
  const [isToCalendarOpen, setIsToCalendarOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState({ current: 0, total: 0 });
  const [internalSelectedLoteIds, setInternalSelectedLoteIds] = useState<number[]>([]);
  const [loteSearchTerm, setLoteSearchTerm] = useState('');
  
  // Função para atualizar lotes selecionados
  const updateSelectedLoteIds = (ids: number[]) => {
    // Sempre usar estado interno quando selecionando dentro do modal
    setInternalSelectedLoteIds(ids);
  };
  
  // Usar estado interno quando não há props ou quando estamos selecionando no modal
  const effectiveSelectedLoteIds = propSelectedLoteIds.length > 0 && loteSelection !== 'specific' 
    ? propSelectedLoteIds 
    : internalSelectedLoteIds;
  
  // Usar effectiveSelectedLoteIds que prioriza seleção interna do modal
  const selectedLoteIds = effectiveSelectedLoteIds;

  const statusOptions = [
    { value: 'all', label: 'Todos os Status' },
    { value: 'PENDENTE', label: 'Pendente' },
    { value: 'FILA', label: 'Fila' },
    { value: 'AGUARDANDO_CONFIRMACAO', label: 'Aguardando Confirmação' },
    { value: 'ENVIADO', label: 'Enviado' },
    { value: 'FALHA', label: 'Falha' },
    { value: 'CONTATO_INVALIDO', label: 'Contato Inválido' },
    { value: 'BAIXADO', label: 'Baixado' }
  ];

  const periodOptions = [
    { value: 'this_month', label: 'Este Mês' },
    { value: 'last_month', label: 'Mês Passado' },
    { value: 'last_3_months', label: 'Últimos 3 Meses' },
    { value: 'last_6_months', label: 'Últimos 6 Meses' },
    { value: 'this_year', label: 'Este Ano' },
    { value: 'last_year', label: 'Ano Passado' },
    { value: 'custom', label: 'Período Personalizado' }
  ];

  // Filtrar lotes para seleção (com busca)
  const filteredLotesForSelection = lotes.filter(lote => {
    if (!loteSearchTerm) return true;
    const searchLower = loteSearchTerm.toLowerCase();
    return (
      lote.id.toString().includes(searchLower) ||
      (lote.tipo_envio && lote.tipo_envio.toLowerCase().includes(searchLower)) ||
      (lote.tag && lote.tag.toLowerCase().includes(searchLower)) ||
      (lote.status && lote.status.toLowerCase().includes(searchLower))
    );
  });

  // Calcular lotes que serão exportados
  const getLotesToExport = (): DisparoLote[] => {
    if (loteSelection === 'all') {
      return lotes;
    } else if (loteSelection === 'specific') {
      return lotes.filter(lote => selectedLoteIds.includes(lote.id));
    } else if (loteSelection === 'period') {
      const now = new Date();
      let dateFrom: string;
      let dateTo: string;

      if (periodFilter === 'custom') {
        if (!customDateFrom || !customDateTo) {
          return [];
        }
        dateFrom = customDateFrom.toISOString().split('T')[0];
        dateTo = customDateTo.toISOString().split('T')[0];
      } else {
        switch (periodFilter) {
          case 'this_month':
            dateFrom = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
            dateTo = now.toISOString().split('T')[0];
            break;
          case 'last_month':
            const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            dateFrom = lastMonth.toISOString().split('T')[0];
            dateTo = new Date(now.getFullYear(), now.getMonth(), 0).toISOString().split('T')[0];
            break;
          case 'last_3_months':
            dateFrom = new Date(now.getFullYear(), now.getMonth() - 3, 1).toISOString().split('T')[0];
            dateTo = now.toISOString().split('T')[0];
            break;
          case 'last_6_months':
            dateFrom = new Date(now.getFullYear(), now.getMonth() - 6, 1).toISOString().split('T')[0];
            dateTo = now.toISOString().split('T')[0];
            break;
          case 'this_year':
            dateFrom = new Date(now.getFullYear(), 0, 1).toISOString().split('T')[0];
            dateTo = now.toISOString().split('T')[0];
            break;
          case 'last_year':
            dateFrom = new Date(now.getFullYear() - 1, 0, 1).toISOString().split('T')[0];
            dateTo = new Date(now.getFullYear() - 1, 11, 31).toISOString().split('T')[0];
            break;
          default:
            return [];
        }
      }

      return lotes.filter(lote => {
        const loteDate = new Date(lote.created_at).toISOString().split('T')[0];
        return loteDate >= dateFrom && loteDate <= dateTo;
      });
    }
    return [];
  };

  // Buscar disparos de um lote
  const fetchLoteDisparos = async (loteId: number): Promise<DisparoBoleto[]> => {
    try {
      const response = await api.get(`/disparos/lotes/${loteId}?limit=0`);
      return (response as any).disparos || [];
    } catch (error) {
      console.error(`Erro ao buscar disparos do lote ${loteId}:`, error);
      return [];
    }
  };

  // Filtrar disparos por status
  const filterDisparosByStatus = (disparos: DisparoBoleto[]): DisparoBoleto[] => {
    if (statusFilter === 'all') {
      return disparos;
    }
    return disparos.filter(disparo => disparo.status === statusFilter);
  };

  // Calcular resumo
  const lotesToExport = getLotesToExport();
  const totalLotes = lotesToExport.length;
  const totalDisparos = lotesToExport.reduce((sum, lote) => sum + (lote.total_boletos || 0), 0);

  // Exportar para PDF - Arquivo único
  const exportSinglePDF = async (allDisparos: Array<{ lote: DisparoLote; disparos: DisparoBoleto[] }>) => {
    const doc = new jsPDF('l', 'mm', 'a4');
    
    let yPosition = 20;
    doc.setFontSize(16);
    doc.text('Relatório Consolidado de Múltiplos Lotes', 14, yPosition);
    yPosition += 10;

    doc.setFontSize(10);
    doc.text(`Total de Lotes: ${allDisparos.length}`, 14, yPosition);
    yPosition += 5;
    doc.text(`Total de Disparos: ${allDisparos.reduce((sum, item) => sum + item.disparos.length, 0)}`, 14, yPosition);
    yPosition += 5;
    doc.text(`Filtro de Status: ${statusFilter === 'all' ? 'Todos' : statusOptions.find(s => s.value === statusFilter)?.label}`, 14, yPosition);
    yPosition += 10;

    // Preparar dados consolidados
    const tableData: any[] = [];
    allDisparos.forEach(({ lote, disparos }) => {
      disparos.forEach(disparo => {
        tableData.push([
          lote.id.toString(),
          disparo.nome,
          disparo.placa || '-',
          disparo.modelo,
          `R$ ${(disparo.valor ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
          disparo.vencimento || '-',
          disparo.contato,
          disparo.status,
          disparo.data_envio && disparo.hora_envio ? `${disparo.data_envio} ${disparo.hora_envio}` : '-'
        ]);
      });
    });

    const headers = ['Lote ID', 'Nome', 'Placa', 'Modelo', 'Valor', 'Vencimento', 'Contato', 'Status', 'Data/Hora Envio'];
    
    autoTable(doc, {
      head: [headers],
      body: tableData,
      startY: yPosition,
      styles: { fontSize: 7 },
      headStyles: { fillColor: [66, 139, 202] },
      alternateRowStyles: { fillColor: [245, 245, 245] }
    });

    const fileName = `lotes_consolidado_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
  };

  // Exportar para PDF - Múltiplos arquivos
  const exportMultiplePDFs = async (allDisparos: Array<{ lote: DisparoLote; disparos: DisparoBoleto[] }>) => {
    const zip = new JSZip();
    
    for (const { lote, disparos } of allDisparos) {
      if (disparos.length === 0) continue;

      const doc = new jsPDF('l', 'mm', 'a4');
      
      doc.setFontSize(16);
      doc.text(`Relatório do Lote ${lote.id}`, 14, 22);
      
      doc.setFontSize(10);
      doc.text(`Tipo de Envio: ${lote.tipo_envio}`, 14, 30);
      doc.text(`Status: ${lote.status}`, 14, 35);
      doc.text(`Total de Disparos: ${disparos.length}`, 14, 40);

      const tableData = disparos.map(disparo => [
        disparo.nome,
        disparo.placa || '-',
        disparo.modelo,
        `R$ ${(disparo.valor ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
        disparo.vencimento || '-',
        disparo.contato,
        disparo.status,
        disparo.data_envio && disparo.hora_envio ? `${disparo.data_envio} ${disparo.hora_envio}` : '-'
      ]);

      const headers = ['Nome', 'Placa', 'Modelo', 'Valor', 'Vencimento', 'Contato', 'Status', 'Data/Hora Envio'];
      
      autoTable(doc, {
        head: [headers],
        body: tableData,
        startY: 50,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [66, 139, 202] },
        alternateRowStyles: { fillColor: [245, 245, 245] }
      });

      const pdfBlob = doc.output('blob');
      zip.file(`lote_${lote.id}.pdf`, pdfBlob);
    }

    const zipBlob = await zip.generateAsync({ type: 'blob' });
    const url = window.URL.createObjectURL(zipBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `lotes_${new Date().toISOString().split('T')[0]}.zip`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // Exportar para Excel - Arquivo único
  const exportSingleXLSX = (allDisparos: Array<{ lote: DisparoLote; disparos: DisparoBoleto[] }>) => {
    const excelData: any[] = [];
    
    allDisparos.forEach(({ lote, disparos }) => {
      disparos.forEach(disparo => {
        excelData.push({
          'Lote ID': lote.id,
          'Nome': disparo.nome,
          'Placa': disparo.placa || '-',
          'Modelo': disparo.modelo,
          'Valor': disparo.valor ?? 0,
          'Vencimento': disparo.vencimento || '-',
          'Contato': disparo.contato,
          'Status': disparo.status,
          'Data Envio': disparo.data_envio && disparo.hora_envio ? `${disparo.data_envio} ${disparo.hora_envio}` : '-'
        });
      });
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(excelData);
    
    const colWidths = [
      { wch: 10 }, // Lote ID
      { wch: 30 }, // Nome
      { wch: 12 }, // Placa
      { wch: 25 }, // Modelo
      { wch: 12 }, // Valor
      { wch: 12 }, // Vencimento
      { wch: 15 }, // Contato
      { wch: 15 }, // Status
      { wch: 18 }  // Data Envio
    ];
    ws['!cols'] = colWidths;
    
    XLSX.utils.book_append_sheet(wb, ws, 'Disparos');
    XLSX.writeFile(wb, `lotes_consolidado_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // Exportar para Excel - Múltiplos arquivos
  const exportMultipleXLSX = async (allDisparos: Array<{ lote: DisparoLote; disparos: DisparoBoleto[] }>) => {
    const zip = new JSZip();
    
    for (const { lote, disparos } of allDisparos) {
      if (disparos.length === 0) continue;

      const excelData = disparos.map(disparo => ({
        'Nome': disparo.nome,
        'Placa': disparo.placa || '-',
        'Modelo': disparo.modelo,
        'Valor': disparo.valor ?? 0,
        'Vencimento': disparo.vencimento || '-',
        'Contato': disparo.contato,
        'Status': disparo.status,
        'Data Envio': disparo.data_envio && disparo.hora_envio ? `${disparo.data_envio} ${disparo.hora_envio}` : '-'
      }));

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);
      
      const colWidths = [
        { wch: 30 }, // Nome
        { wch: 12 }, // Placa
        { wch: 25 }, // Modelo
        { wch: 12 }, // Valor
        { wch: 12 }, // Vencimento
        { wch: 15 }, // Contato
        { wch: 15 }, // Status
        { wch: 18 }  // Data Envio
      ];
      ws['!cols'] = colWidths;
      
      XLSX.utils.book_append_sheet(wb, ws, 'Disparos');
      const excelBuffer = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
      zip.file(`lote_${lote.id}.xlsx`, excelBuffer);
    }

    const zipBlob = await zip.generateAsync({ type: 'blob' });
    const url = window.URL.createObjectURL(zipBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `lotes_${new Date().toISOString().split('T')[0]}.zip`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // Função principal de exportação
  const handleExport = async () => {
    if (loteSelection === 'specific' && selectedLoteIds.length === 0) {
      toast({
        title: "Aviso",
        description: "Selecione pelo menos um lote para exportar",
        variant: "destructive"
      });
      return;
    }

    if (loteSelection === 'period' && periodFilter === 'custom' && (!customDateFrom || !customDateTo)) {
      toast({
        title: "Aviso",
        description: "Selecione o período personalizado (data inicial e final)",
        variant: "destructive"
      });
      return;
    }

    if (totalLotes === 0) {
      toast({
        title: "Aviso",
        description: "Nenhum lote encontrado com os filtros aplicados",
        variant: "destructive"
      });
      return;
    }

    setIsExporting(true);
    setExportProgress({ current: 0, total: totalLotes });

    try {
      // Buscar disparos de todos os lotes
      const allDisparosData: Array<{ lote: DisparoLote; disparos: DisparoBoleto[] }> = [];
      
      for (let i = 0; i < lotesToExport.length; i++) {
        const lote = lotesToExport[i];
        setExportProgress({ current: i + 1, total: totalLotes });
        
        const disparos = await fetchLoteDisparos(lote.id);
        const filteredDisparos = filterDisparosByStatus(disparos);
        
        allDisparosData.push({ lote, disparos: filteredDisparos });
      }

      // Executar exportação
      if (exportFormat === 'pdf') {
        if (exportOrganization === 'single') {
          await exportSinglePDF(allDisparosData);
        } else {
          await exportMultiplePDFs(allDisparosData);
        }
      } else {
        if (exportOrganization === 'single') {
          exportSingleXLSX(allDisparosData);
        } else {
          await exportMultipleXLSX(allDisparosData);
        }
      }

      toast({
        title: "Sucesso",
        description: `Exportação concluída! ${totalLotes} lote(s) exportado(s).`,
      });

      onClose();
    } catch (error) {
      console.error('Erro ao exportar lotes:', error);
      toast({
        title: "Erro",
        description: "Erro ao exportar lotes. Tente novamente.",
        variant: "destructive"
      });
    } finally {
      setIsExporting(false);
      setExportProgress({ current: 0, total: 0 });
    }
  };

  // Reset quando modal fechar
  useEffect(() => {
    if (!isOpen) {
      setExportFormat('pdf');
      setExportOrganization('single');
      setLoteSelection('all');
      setPeriodFilter('this_month');
      setCustomDateFrom(undefined);
      setCustomDateTo(undefined);
      setStatusFilter('all');
      setInternalSelectedLoteIds([]);
      setLoteSearchTerm('');
      if (onSelectionModeChange) {
        onSelectionModeChange(false);
      }
    }
  }, [isOpen, onSelectionModeChange]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Exportar Lotes</DialogTitle>
          <DialogDescription>
            Selecione o formato, organização e filtros para exportação de múltiplos lotes
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Seleção de Formato */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Formato de Exportação</Label>
            <div className="grid grid-cols-2 gap-3">
              <Button
                variant={exportFormat === 'pdf' ? 'default' : 'outline'}
                onClick={() => setExportFormat('pdf')}
                className="flex items-center gap-2"
                disabled={isExporting}
              >
                <FileText className="h-4 w-4" />
                PDF
              </Button>
              <Button
                variant={exportFormat === 'xlsx' ? 'default' : 'outline'}
                onClick={() => setExportFormat('xlsx')}
                className="flex items-center gap-2"
                disabled={isExporting}
              >
                <Database className="h-4 w-4" />
                Excel
              </Button>
            </div>
          </div>

          {/* Organização */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Organização</Label>
            <RadioGroup value={exportOrganization} onValueChange={(value) => setExportOrganization(value as 'single' | 'multiple')}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="single" id="single" disabled={isExporting} />
                <Label htmlFor="single" className="cursor-pointer">Arquivo único consolidado</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="multiple" id="multiple" disabled={isExporting} />
                <Label htmlFor="multiple" className="cursor-pointer">Múltiplos arquivos (ZIP)</Label>
              </div>
            </RadioGroup>
          </div>

            {/* Seleção de Lotes */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Seleção de Lotes</Label>
            <RadioGroup value={loteSelection} onValueChange={(value) => {
              const newValue = value as 'all' | 'period' | 'specific';
              setLoteSelection(newValue);
              // Não precisamos mais ativar modo de seleção na tabela
              // A seleção será feita diretamente no modal
            }}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="all" id="all" disabled={isExporting} />
                <Label htmlFor="all" className="cursor-pointer">Todos os lotes ({lotes.length})</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="period" id="period" disabled={isExporting} />
                <Label htmlFor="period" className="cursor-pointer">Por período</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="specific" id="specific" disabled={isExporting} />
                <Label htmlFor="specific" className="cursor-pointer">Lotes específicos ({selectedLoteIds.length} selecionados)</Label>
              </div>
            </RadioGroup>

            {/* Período */}
            {loteSelection === 'period' && (
              <div className="ml-6 space-y-3">
                <Select value={periodFilter} onValueChange={setPeriodFilter} disabled={isExporting}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {periodOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {periodFilter === 'custom' && (
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">De:</Label>
                      <Popover open={isFromCalendarOpen} onOpenChange={setIsFromCalendarOpen}>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="w-full justify-start text-left font-normal" disabled={isExporting}>
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {customDateFrom ? format(customDateFrom, 'dd/MM/yyyy') : 'Data início'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={customDateFrom}
                            onSelect={(date) => {
                              setCustomDateFrom(date);
                              setIsFromCalendarOpen(false);
                            }}
                            locale={ptBR}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div>
                      <Label className="text-xs">Até:</Label>
                      <Popover open={isToCalendarOpen} onOpenChange={setIsToCalendarOpen}>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="w-full justify-start text-left font-normal" disabled={isExporting}>
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {customDateTo ? format(customDateTo, 'dd/MM/yyyy') : 'Data fim'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={customDateTo}
                            onSelect={(date) => {
                              setCustomDateTo(date);
                              setIsToCalendarOpen(false);
                            }}
                            locale={ptBR}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Lotes específicos */}
            {loteSelection === 'specific' && (
              <div className="ml-6 space-y-3">
                {/* Busca de lotes */}
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Buscar lotes..."
                    value={loteSearchTerm}
                    onChange={(e) => setLoteSearchTerm(e.target.value)}
                    className="pl-10"
                    disabled={isExporting}
                  />
                </div>

                {/* Selecionar todos */}
                <div className="flex items-center space-x-2 p-2 bg-gray-50 rounded-lg">
                  <Checkbox
                    checked={selectedLoteIds.length === filteredLotesForSelection.length && filteredLotesForSelection.length > 0}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        updateSelectedLoteIds(filteredLotesForSelection.map(l => l.id));
                      } else {
                        updateSelectedLoteIds([]);
                      }
                    }}
                    disabled={isExporting}
                  />
                  <Label className="text-sm font-medium cursor-pointer">
                    Selecionar todos ({filteredLotesForSelection.length})
                  </Label>
                </div>

                {/* Lista de lotes */}
                <ScrollArea className="h-[200px] border rounded-lg p-2">
                  <div className="space-y-2">
                    {filteredLotesForSelection.length === 0 ? (
                      <p className="text-sm text-gray-500 text-center py-4">
                        Nenhum lote encontrado
                      </p>
                    ) : (
                      filteredLotesForSelection.map((lote) => (
                        <div
                          key={lote.id}
                          className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded-lg cursor-pointer"
                          onClick={() => {
                            if (isExporting) return;
                            if (selectedLoteIds.includes(lote.id)) {
                              updateSelectedLoteIds(selectedLoteIds.filter(id => id !== lote.id));
                            } else {
                              updateSelectedLoteIds([...selectedLoteIds, lote.id]);
                            }
                          }}
                        >
                          <Checkbox
                            checked={selectedLoteIds.includes(lote.id)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                updateSelectedLoteIds([...selectedLoteIds, lote.id]);
                              } else {
                                updateSelectedLoteIds(selectedLoteIds.filter(id => id !== lote.id));
                              }
                            }}
                            disabled={isExporting}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm">Lote #{lote.id}</span>
                              <Badge variant="outline" className="text-xs">
                                {lote.tipo_envio}
                              </Badge>
                              {lote.status && (
                                <Badge variant="secondary" className="text-xs">
                                  {lote.status}
                                </Badge>
                              )}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              {lote.total_boletos || 0} disparos • {new Intl.NumberFormat('pt-BR', {
                                style: 'currency',
                                currency: 'BRL'
                              }).format(lote.valor_total || 0)}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </ScrollArea>

                {selectedLoteIds.length > 0 && (
                  <p className="text-sm text-gray-600">
                    {selectedLoteIds.length} lote(s) selecionado(s)
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Filtro por Status */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Filtrar por Status</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter} disabled={isExporting}>
              <SelectTrigger>
                <SelectValue placeholder="Selecionar status" />
              </SelectTrigger>
              <SelectContent>
                {statusOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Resumo */}
          <div className="bg-gray-50 p-3 rounded-lg">
            <div className="text-sm text-gray-600 space-y-1">
              <p><strong>Total de lotes:</strong> {totalLotes}</p>
              <p><strong>Total de disparos:</strong> {totalDisparos}</p>
              <p><strong>Serão exportados:</strong> {statusFilter === 'all' ? totalDisparos : 'Calculado durante exportação'}</p>
              <p><strong>Formato:</strong> {exportFormat.toUpperCase()}</p>
              <p><strong>Organização:</strong> {exportOrganization === 'single' ? 'Arquivo único' : 'Múltiplos arquivos (ZIP)'}</p>
            </div>
          </div>

          {/* Progresso */}
          {isExporting && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span>Exportando lotes...</span>
                <span>{exportProgress.current} / {exportProgress.total}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all"
                  style={{ width: `${(exportProgress.current / exportProgress.total) * 100}%` }}
                />
              </div>
            </div>
          )}

          {/* Botões de Ação */}
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={onClose} disabled={isExporting}>
              Cancelar
            </Button>
            <Button onClick={handleExport} disabled={isExporting || totalLotes === 0} className="flex items-center gap-2">
              {isExporting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Exportando...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4" />
                  Exportar
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ExportarLotesModal;

